import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { createTw } from "react-pdf-tailwind";
import { convertToUpper } from "../../utils/convertToUpper";

const tw = createTw({
  theme: {
    fontFamily: { sans: "Helvetica" },
    extend: {
      colors: {
        brand: "#003366",
        accent: "#0056b3",
        headerGray: "#f8f9fa",
        borderGray: "#d1d5db",
        successLight: "#f0fdf4",
        successBorder: "#22c55e",
      },
    },
  },
});

const styles = StyleSheet.create({
  pageContent: {
    padding: 24,
    fontFamily: "Helvetica",
    fontSize: 6,
    lineHeight: 1.2,
    color: "#1f2937",
    textTransform: "uppercase",
  },
  recommendedColumn: {
    backgroundColor: "#f0f9ff",
  },
  signatureBlock: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderStyle: "solid",
    borderRadius: 2,
    overflow: "hidden",
  },
  borderBottomBrand: {
    borderBottomWidth: 2,
    borderBottomColor: "#003366",
    borderBottomStyle: "solid",
  },
  borderAllGray: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderStyle: "solid",
  },
  textWhite: {
    color: "#ffffff",
  },
  textDark: {
    color: "#1f2937",
  },
  textGray: {
    color: "#4b5563",
  },
  bgBrand: {
    backgroundColor: "#003366",
  },
  bgAccent: {
    backgroundColor: "#0056b3",
  },
  bgGray50: {
    backgroundColor: "#f8f9fa",
  },
  bgGray100: {
    backgroundColor: "#f3f4f6",
  },
});

const InfoField = ({ label, value, width = "48%", labelWidth = 110 }) => (
  <View style={[tw("flex flex-row mb-1"), { width }]}>
    <Text style={{ width: labelWidth, fontWeight: "bold", color: "#4b5563" }}>
      {label}:
    </Text>
    <Text style={tw("flex-1 text-gray-800")}>{convertToUpper(value)}</Text>
  </View>
);

const InfoSectionTitle = ({ children }) => (
  <View
    style={[
      tw("mb-2 pb-1"),
      {
        borderBottomWidth: 1,
        borderBottomColor: "#e5e7eb",
        borderBottomStyle: "solid",
      },
    ]}
  >
    <Text style={tw("font-bold text-[7px] text-brand uppercase")}>
      {children}
    </Text>
  </View>
);

const normalizeAuthorizationColumns = (firmas = []) => {
  const findByLabel = (keywords) =>
    firmas.find((item) => {
      const label = String(item?.firma || "").toUpperCase();
      return keywords.some((keyword) => label.includes(keyword));
    });

  const aprobado = findByLabel(["APROBADO"]);

  return [
    {
      title: "APROBADO POR:",
      nombre: aprobado?.nombre || firmas?.[0]?.nombre || "",
      cargo: aprobado?.cargo || firmas?.[0]?.cargo || "",
      fecha: aprobado?.fecha || firmas?.[0]?.fecha || "",
    },
  ];
};

const formatUsd = (value) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) return "-";

  return numberValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDeliveryTime = (value) => {
  if (!value || value === "-") {
    return "-";
  }

  return convertToUpper(`${value} días`);
};

const CuadroComparativoTransportePDF = ({ info }) => {
  const providers = info?.proveedores || [];
  const recommendedId = info?.recomendado?.id;
  const hasRecommendedProvider = Boolean(info?.recomendado);
  const activeProviders = providers
    .filter((prov) => prov && prov.nombre && prov.nombre.trim() !== "")
    .map((prov) => ({
      ...prov,
      precio: prov.precio !== undefined ? prov.precio : prov.cUnit || 0,
      cantidad: prov.cantidad !== undefined ? prov.cantidad : 1,
      total:
        prov.total !== undefined
          ? prov.total
          : Number(prov.cUnit || 0) * Number(prov.cantidad || 1),
      tiempoEntrega: prov.tiempoEntrega || prov.deliveryDate || "-",
      formaPago: prov.formaPago || prov.paymentTerms || "-",
    }));

  const generalInfo = info?.general || {
    numeroImportacion: info?.nroPedido || "-",
    proveedor: "-",
    producto: info?.descripcion || "-",
    incoterms: info?.incotermServicio || "-",
    fechaElaboracion: info?.fechaInicio || "-",
  };

  const operativeInfo = info?.operativa || {
    lugarRecojo: info?.direccionRecojo || "-",
    lugarEntrega: info?.lugarEntrega || "-",
    fechaRecojo: info?.fechaRecojo || "-",
    modalidadDespacho: info?.tipoDespacho || "-",
    modalidadCarga: "-",
    pesoBrutoKg: info?.pesoBruto || "-",
    cantidadTipoContenedores: info?.cantidad || "-",
  };

  const authorizationColumns = normalizeAuthorizationColumns(
    info?.firmas || [],
  );

  return (
    <Document>
      <Page size="LETTER" orientation="landscape" style={styles.pageContent}>
        {/* ENCABEZADO PREMIUM */}
        <View
          style={[
            tw("flex flex-row items-center justify-between mb-4 pb-2"),
            styles.borderBottomBrand,
          ]}
        >
          <View style={tw("w-[25%]")}>
            <Image
              src="/logoPlastiforte.png"
              style={{ width: 120, height: 40, objectFit: "contain" }}
            />
          </View>
          <View style={tw("w-[50%] flex flex-col items-center")}>
            <Text
              style={tw(
                "font-bold text-[10px] text-brand uppercase tracking-wider mb-2",
              )}
            >
              CUADRO COMPARATIVO DE TRANSPORTE INTERNACIONAL
            </Text>
            <Text style={tw("text-[7px] text-gray-500 font-bold")}>
              PROCESO DE ADQUISICIONES
            </Text>
          </View>
          <View style={tw("w-[25%] flex flex-col items-end")}>
            <Text style={tw("font-bold text-[7px]")}>
              CÓDIGO:{" "}
              <Text style={tw("font-normal")}>{info?.codigoDoc || "-"}</Text>
            </Text>
            <Text style={tw("font-bold text-[7px]")}>
              VERSIÓN:{" "}
              <Text style={tw("font-normal")}>{info?.versionDoc || "-"}</Text>
            </Text>
          </View>
        </View>

        {/* INFORMACIÓN GENERAL Y OPERATIVA */}
        <View style={tw("flex flex-col mb-4 bg-gray-50 p-2 rounded")}>
          <InfoSectionTitle>
            Información general de la operación
          </InfoSectionTitle>

          <View style={tw("flex flex-row w-full justify-between mb-1")}>
            <InfoField
              label="NRO. IMPORTACIÓN"
              value={generalInfo.numeroImportacion}
              width="70%"
              labelWidth={78}
            />

            <InfoField
              label="FECHA ELABORACIÓN"
              value={generalInfo.fechaElaboracion}
              width="30%"
              labelWidth={98}
            />
          </View>

          <View style={tw("flex flex-row w-full justify-between mb-2")}>
            <InfoField
              label="PROVEEDOR"
              value={generalInfo.proveedor}
              width="70%"
              labelWidth={79}
            />

            <InfoField
              label="INCOTERMS"
              value={generalInfo.incoterms}
              width="30%"
              labelWidth={95}
            />
          </View>

          <View style={tw("flex flex-row w-full justify-between mb-2")}>
            <InfoField
              label="PRODUCTO"
              value={generalInfo.producto}
              width="100%"
              labelWidth={90}
            />
          </View>

          <InfoSectionTitle>
            Información operativa del servicio de transporte
          </InfoSectionTitle>

          <View style={tw("flex flex-row w-full justify-between mb-1")}>
            <InfoField
              label="LUGAR DE RECOJO"
              value={operativeInfo.lugarRecojo}
              width="30%"
              labelWidth={90}
            />

            <InfoField
              label="MODALIDAD DESPACHO"
              value={operativeInfo.modalidadDespacho}
              width="40%"
              labelWidth={105}
            />

            <InfoField
              label="PESO BRUTO KG"
              value={operativeInfo.pesoBrutoKg}
              width="30%"
              labelWidth={78}
            />
          </View>

          <View style={tw("flex flex-row w-full justify-between mb-1")}>
            <InfoField
              label="LUGAR DE ENTREGA"
              value={operativeInfo.lugarEntrega}
              width="70%"
              labelWidth={90}
            />

            <InfoField
              label="CANTIDAD Y TIPO DE CONTENEDORES"
              value={operativeInfo.cantidadTipoContenedores}
              width="30%"
              labelWidth={150}
            />
          </View>

          <View style={tw("flex flex-row w-full justify-between")}>
            <InfoField
              label="FECHA DE RECOJO"
              value={operativeInfo.fechaRecojo}
              width="30%"
              labelWidth={90}
            />

            <InfoField
              label="MODALIDAD DE CARGA"
              value={operativeInfo.modalidadCarga}
              width="70%"
              labelWidth={104}
            />
          </View>
        </View>

        {/* TABLA DE COMPARACIÓN */}
        <View
          style={[tw("rounded-sm overflow-hidden mb-4"), styles.borderAllGray]}
        >
          {/* Header de la Tabla */}
          <View
            style={[
              tw("flex flex-row items-center w-full"),
              styles.bgBrand,
              {
                borderBottomWidth: 1,
                borderBottomColor: "#ffffff",
                borderBottomStyle: "solid",
              },
            ]}
          >
            <View
              style={[
                tw("p-1 items-center justify-center"),
                {
                  width: "5%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text
                style={[
                  tw("font-bold text-[7px] text-center"),
                  styles.textWhite,
                ]}
              >
                Item
              </Text>
            </View>

            <View
              style={[
                tw("p-1 justify-center"),
                {
                  width: "31%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold text-[7px] ml-1"), styles.textWhite]}>
                Proveedor
              </Text>
            </View>

            <View
              style={[
                tw("p-1 items-end justify-center pr-2"),
                {
                  width: "12%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text
                style={[
                  tw("font-bold text-[7px] text-right"),
                  styles.textWhite,
                ]}
              >
                Precio
              </Text>
            </View>

            <View
              style={[
                tw("p-1 items-center justify-center"),
                {
                  width: "10%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text
                style={[
                  tw("font-bold text-[7px] text-center"),
                  styles.textWhite,
                ]}
              >
                Cantidad
              </Text>
            </View>

            <View
              style={[
                tw("p-1 items-end justify-center pr-2"),
                {
                  width: "13%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text
                style={[
                  tw("font-bold text-[7px] text-right"),
                  styles.textWhite,
                ]}
              >
                Total
              </Text>
            </View>

            <View
              style={[
                tw("p-1 items-center justify-center"),
                {
                  width: "14%",
                  borderRightWidth: 1,
                  borderRightColor: "#ffffff",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text
                style={[
                  tw("font-bold text-[7px] text-center"),
                  styles.textWhite,
                ]}
              >
                Tiempo de Entrega
              </Text>
            </View>

            <View
              style={[
                tw("p-1 items-center justify-center"),
                {
                  width: "15%",
                },
              ]}
            >
              <Text
                style={[
                  tw("font-bold text-[7px] text-center"),
                  styles.textWhite,
                ]}
              >
                Forma de Pago
              </Text>
            </View>
          </View>

          {activeProviders.length > 0 ? (
            activeProviders.map((prov, index) => {
              const total = Number(prov.precio) * Number(prov.cantidad);
              const isRecommended =
                prov.id === recommendedId || prov.isRecommended;

              return (
                <View
                  key={prov.id || index}
                  style={[
                    tw("flex flex-row w-full"),
                    isRecommended
                      ? styles.recommendedColumn
                      : index % 2 !== 0
                        ? styles.bgGray50
                        : {},
                    {
                      borderBottomWidth:
                        index === activeProviders.length - 1 ? 0 : 1,
                      borderBottomColor: "#d1d5db",
                      borderBottomStyle: "solid",
                    },
                  ]}
                >
                  <View
                    style={[
                      tw("p-1 items-center justify-center"),
                      {
                        width: "5%",
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] text-center"),
                        styles.textDark,
                        isRecommended ? tw("font-bold") : {},
                      ]}
                    >
                      {index + 1}
                    </Text>
                  </View>

                  <View
                    style={[
                      tw("p-1 justify-center"),
                      {
                        width: "31%",
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] ml-1"),
                        styles.textDark,
                        isRecommended ? tw("font-bold") : {},
                      ]}
                    >
                      {convertToUpper(prov.nombre)}
                    </Text>
                  </View>

                  <View
                    style={[
                      tw("p-1 items-end justify-center pr-2"),
                      {
                        width: "12%",
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] text-right"),
                        styles.textDark,
                        isRecommended ? tw("font-bold") : {},
                      ]}
                    >
                      {Number(prov.precio).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </View>

                  <View
                    style={[
                      tw("p-1 items-center justify-center"),
                      {
                        width: "10%",
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] text-center"),
                        styles.textDark,
                        isRecommended ? tw("font-bold") : {},
                      ]}
                    >
                      {Number(prov.cantidad).toFixed(2)}
                    </Text>
                  </View>

                  <View
                    style={[
                      tw("p-1 items-end justify-center pr-2"),
                      {
                        width: "13%",
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] text-right font-bold"),
                        isRecommended ? tw("text-brand") : styles.textDark,
                      ]}
                    >
                      {total.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  </View>

                  <View
                    style={[
                      tw("p-1 items-center justify-center"),
                      {
                        width: "14%",
                        borderRightWidth: 1,
                        borderRightColor: "#d1d5db",
                        borderRightStyle: "solid",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] text-center"),
                        styles.textDark,
                        isRecommended ? tw("font-bold") : {},
                      ]}
                    >
                      {formatDeliveryTime(prov.tiempoEntrega)}
                    </Text>
                  </View>

                  <View
                    style={[
                      tw("p-1 items-center justify-center"),
                      {
                        width: "15%",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        tw("text-[7px] text-center"),
                        styles.textDark,
                        isRecommended ? tw("font-bold") : {},
                      ]}
                    >
                      {convertToUpper(prov.formaPago)}
                    </Text>
                  </View>
                </View>
              );
            })
          ) : (
            <View
              style={tw(
                "flex flex-row w-full py-3 items-center justify-center",
              )}
            >
              <Text style={[tw("text-[8px]"), styles.textGray]}>
                No hay proveedores registrados
              </Text>
            </View>
          )}
        </View>

        {/* RESULTADO DE LA EVALUACIÓN */}
        <View
          style={[
            tw("flex flex-col mb-4 rounded overflow-hidden"),
            {
              borderWidth: 1,
              borderColor: "#d1d5db",
              borderStyle: "solid",
            },
          ]}
        >
          <View style={[tw("p-1"), styles.bgBrand]}>
            <Text
              style={[tw("font-bold text-[7px] uppercase"), styles.textWhite]}
            >
              Resultado de la Evaluación
            </Text>
          </View>

          <View style={tw("flex flex-row")}>
            <View
              style={[
                tw("p-2"),
                {
                  width: "35%",
                  borderRightWidth: 1,
                  borderRightColor: "#d1d5db",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold text-[6px] mb-1"), styles.textGray]}>
                PROVEEDOR SELECCIONADO
              </Text>
              <Text style={[tw("font-bold text-[8px]"), styles.textDark]}>
                {hasRecommendedProvider
                  ? convertToUpper(info.recomendado.nombre)
                  : "FALTA SELECCIONAR PROVEEDOR"}
              </Text>
            </View>

            <View
              style={[
                tw("p-2"),
                {
                  width: "20%",
                  borderRightWidth: 1,
                  borderRightColor: "#d1d5db",
                  borderRightStyle: "solid",
                },
              ]}
            >
              <Text style={[tw("font-bold text-[6px] mb-1"), styles.textGray]}>
                TOTAL USD
              </Text>
              <Text style={[tw("font-bold text-[8px] text-brand")]}>
                {hasRecommendedProvider
                  ? formatUsd(info.recomendado.total)
                  : "-"}
              </Text>
            </View>

            <View style={[tw("p-2"), { width: "45%" }]}>
              <Text style={[tw("font-bold text-[6px] mb-1"), styles.textGray]}>
                JUSTIFICACIÓN DE LA SELECCIÓN
              </Text>
              <Text style={[tw("text-[6px]"), styles.textDark]}>
                {hasRecommendedProvider
                  ? convertToUpper(info?.justificacion)
                  : "PENDIENTE DE SELECCIÓN DE PROVEEDOR."}
              </Text>
            </View>
          </View>

          <View
            style={[
              tw("p-2"),
              {
                borderTopWidth: 1,
                borderTopColor: "#d1d5db",
                borderTopStyle: "solid",
              },
            ]}
          >
            <Text style={[tw("font-bold text-[6px] mb-1"), styles.textGray]}>
              OBSERVACIONES
            </Text>
            <Text style={[tw("text-[6px]"), styles.textDark]}>
              {hasRecommendedProvider
                ? convertToUpper(
                    info?.observaciones || info?.recomendado?.observaciones,
                  )
                : "DEBE SELECCIONARSE UN PROVEEDOR PARA COMPLETAR EL RESULTADO DE LA EVALUACIÓN."}
            </Text>
          </View>
        </View>

        {/* AUTORIZACIONES */}
        <View style={tw("flex-grow")} />

        <View
          style={[
            tw("mb-1 p-1"),
            {
              borderWidth: 1,
              borderColor: "#d1d5db",
              borderStyle: "solid",
              backgroundColor: "#f8f9fa",
            },
          ]}
        >
          <Text style={[tw("font-bold text-[7px] uppercase"), styles.textGray]}>
            Autorizaciones
          </Text>
        </View>

        <View
          style={[
            tw("flex-row"),
            {
              borderWidth: 1,
              borderColor: "#d1d5db",
              borderStyle: "solid",
            },
          ]}
        >
          {authorizationColumns.map((item, index) => (
            <View
              key={item.title}
              style={[
                { width: "100%" },
                index !== authorizationColumns.length - 1
                  ? {
                      borderRightWidth: 1,
                      borderRightColor: "#d1d5db",
                      borderRightStyle: "solid",
                    }
                  : {},
              ]}
            >
              <View
                style={[
                  tw("p-1"),
                  {
                    height: 32,
                    borderBottomWidth: 1,
                    borderBottomColor: "#d1d5db",
                    borderBottomStyle: "solid",
                  },
                ]}
              >
                <Text style={[tw("font-bold text-[6px]"), styles.textDark]}>
                  FIRMA
                </Text>
              </View>

              <View
                style={[
                  tw("p-1"),
                  {
                    minHeight: 22,
                    borderBottomWidth: 1,
                    borderBottomColor: "#d1d5db",
                    borderBottomStyle: "solid",
                  },
                ]}
              >
                <Image
                  src={"/images/firma-representante-plastiforte.png"}
                  style={{
                    width: 100,
                    height: 100,
                    marginTop: -60,
                    marginBottom: -40,
                    objectFit: "contain",
                    paddingLeft: 5,
                  }}
                />
                <Text style={[tw("font-bold text-[6px]"), styles.textDark]}>
                  {item.title}
                </Text>
                <Text style={[tw("font-bold text-[7px]"), styles.textDark]}>
                  {/* {convertToUpper(item.nombre)} */}
                  GABRIELA MIRANDA GARCIA
                </Text>
              </View>

              <View
                style={[
                  tw("p-1"),
                  {
                    minHeight: 22,
                    borderBottomWidth: 1,
                    borderBottomColor: "#d1d5db",
                    borderBottomStyle: "solid",
                  },
                ]}
              >
                <Text style={[tw("font-bold text-[6px]"), styles.textDark]}>
                  CARGO
                </Text>
                <Text style={[tw("text-[6px]"), styles.textDark]}>
                  {/* {convertToUpper(item.cargo)} */}
                  RESPONSABLE DE IMPORTACIONES
                </Text>
              </View>

              <View style={tw("p-1")}>
                <Text style={[tw("font-bold text-[6px]"), styles.textDark]}>
                  FECHA
                </Text>
                <Text style={[tw("text-[6px]"), styles.textDark]}>
                  {convertToUpper(item.fecha)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default CuadroComparativoTransportePDF;
